import { Fragment } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'

import { ChartAnnotation } from '@/components/elements/ChartAnnotation'
import { Spacer } from '@/components/elements/Spacer'
import {
  Table,
  Thead,
  Tr,
  Th,
  Td,
  TbodyWithThreeColumnHeadings,
} from '@/components/elements/Table'
import { AHeading, BHeading } from '@/components/enlayouts/Ir/Heading'
import { Annotation, Paragraph } from '@/components/enlayouts/Ir/Paragraph'

import { MonthlySales } from '@/types/monthlySales'

type Props = {
  monthlySales: MonthlySales
}

// 月の数字を英語表記に変換する関数
const monthNumberToEnglish = (monthNumber: number) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[monthNumber - 1];
};

const dayNumberToEnglish = (dayNumber: string): string => {
   /// dayNumberが1桁かつ10の位が0の場合
   if (dayNumber.length === 2 && dayNumber[0] === '0') {
    // 一の位の数字のみを取り出す
   return parseInt(dayNumber[1]).toString();
  }
  return dayNumber;
};

export const MonthlySalesTable = ({ monthlySales }: Props) => {
  return (
    <>
      <AHeading>{monthlySales.titleEN}</AHeading>
      <Paragraph>Updated around the 10th of each month.</Paragraph>
      <Spacer h={{ sp: 10, pc: 20 }} />
      <PublicationDate>
        {monthNumberToEnglish(parseInt(formatDate(monthlySales.newpublicationDate, 'MM'), 10))} {' '}
        {dayNumberToEnglish(formatDate(monthlySales.newpublicationDate,'dd'))},{' '}
        {formatDate(monthlySales.newpublicationDate,'yyyy')} 
        <br />
        (YoY : %)
      </PublicationDate>
      <Spacer h={{ sp: 10, pc: 20 }} />
      <section>
        <Table>
          <>
            <Thead>
              <Tr>
                <Th rowSpan={2} colSpan={3}/>
                <Th colSpan={9} color={'secondary'}>
                  {monthlySales.fHyear.replace('年', '')}
                </Th>
                <Th colSpan={9} color={'secondary'}>
                  {monthlySales.sHyear.replace('年', '')}
                </Th>
                <Th rowSpan={2} color={'secondary'}>
                 Full
                </Th>
              </Tr>
              <Tr>
                <Th color="quaternary">Jul.</Th>
                <Th color="quaternary">Aug.</Th>
                <Th color="quaternary">Sep.</Th>
                <QuarterTh color="tertiary">Q1</QuarterTh>
                <Th color="quaternary">Oct.</Th>
                <Th color="quaternary">Nov.</Th>
                <Th color="quaternary">Dec.</Th>
                <QuarterTh color="tertiary">Q2</QuarterTh>
                <Th color="secondary">1H</Th>
                <Th color="quaternary">Jan.</Th>
                <Th color="quaternary">Feb.</Th>
                <Th color="quaternary">Mar.</Th>
                <QuarterTh color="tertiary">Q3</QuarterTh>
                <Th color="quaternary">Apr.</Th>
                <Th color="quaternary">May</Th>
                <Th color="quaternary">Jun.</Th>
                <QuarterTh color="tertiary">Q4</QuarterTh>
                <Th color="secondary">2H</Th>
              </Tr>
            </Thead>
            <TbodyWithThreeColumnHeadings firstHeadWidth="35px" secondHeadWidth='35px'>
              <Tr>
                <Th rowSpan={7} noBorder>
                  <RotatedHeaderLabel>TRIAL</RotatedHeaderLabel>
                </Th>
                <Th rowSpan={3} >
                  <RotatedHeaderLabel>Comp. Stores</RotatedHeaderLabel>
                </Th>
                <SmallTh>Sales</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.oShopSales ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh2><NoWrapLabel>Customer Traffic</NoWrapLabel></SmallTh2>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.oShopCustomers ?? ' '}
                  </SmallTd2>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.oShopCustomers ?? ' '}
                  </SmallTd2>
                ))}
                <SmallTd2>{monthlySales.fullYear.oShopCustomers  ?? ' '}</SmallTd2>
              </Tr>
              <Tr>
                <SmallTh2>Average Spending</SmallTh2>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.oShopCustomerUnitPrice ?? ' '}
                  </SmallTd2>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.oShopCustomerUnitPrice ?? ' '}
                  </SmallTd2>
                ))}
                <SmallTd2>{monthlySales.fullYear.oShopCustomerUnitPrice ?? ' '}</SmallTd2>         
              </Tr> 
              <Tr>
                <SmallTh rowSpan={4}>
                  <RotatedHeaderLabel>All Stores</RotatedHeaderLabel>
                </SmallTh>
                <SmallTh>Sales</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.aShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.aShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.aShopSales ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh>Store Opening</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.newOpenShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.newOpenShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.newOpenShops}
                </SmallTd>
              </Tr>
              {/* <Tr>
                <SmallTh>Format conversion (increase)*</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.toTrialSeiyu}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.toTrialSeiyu}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.toTrialSeiyu}
                </SmallTd>
              </Tr> */}
              <Tr>
                <SmallTh>Store Closure</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.closeShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.closeShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.closeShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>Store Count</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.allShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.allShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.allShops}
                </SmallTd>
              </Tr>

              <Tr>
                <Th rowSpan={9} noBorder style={{whiteSpace: 'nowrap'}}>
                  <RotatedHeaderLabel>SEIYU</RotatedHeaderLabel>
                </Th>
                <Th rowSpan={3} style={{whiteSpace:'nowrap'}}>
                  <RotatedHeaderLabel>Comp. Stores</RotatedHeaderLabel>
                </Th>
                <SmallTh>Sales</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.seiyuOShopSales ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh2><NoWrapLabel>Customer Traffic</NoWrapLabel></SmallTh2>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomers ?? ' '}
                  </SmallTd2>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomers ?? ' '}
                  </SmallTd2>
                ))}
                <SmallTd2>{monthlySales.fullYear.seiyuOShopCustomers  ?? ' '}</SmallTd2>
              </Tr>
              <Tr>
                <SmallTh2>Average Spending</SmallTh2>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomerUnitPrice ?? ' '}
                  </SmallTd2>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd2 key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomerUnitPrice ?? ' '}
                  </SmallTd2>
                ))}
                <SmallTd2>{monthlySales.fullYear.seiyuOShopCustomerUnitPrice ?? ' '}</SmallTd2>         
              </Tr>
              <Tr>
                <SmallTh rowSpan={6}>
                  <RotatedHeaderLabel>All Stores</RotatedHeaderLabel>
                </SmallTh>
                <SmallTh>Sales</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.seiyuAShopSales ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh>Store Opening</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuNewOpenShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuNewOpenShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuNewOpenShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>Store Closure</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuCloseShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuCloseShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuCloseShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>Store Count</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuAllShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuAllShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuAllShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>Format conversion<br />(SEIYU to <span style={{whiteSpace: 'nowrap'}}>TRIAL SEIYU)</span></SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuToTrial}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuToTrial}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuToTrial}
                </SmallTd>
              </Tr>
            </TbodyWithThreeColumnHeadings>
          </>
        </Table>
      </section>
      <Spacer h={{ sp: 15, pc: 20 }} />
      <Paragraph>*YoY sales growth are preliminary figures based on POS data. They may differ from book closing figures.</Paragraph>
      <Paragraph>*Product categories are as follows. Grocery: Processed food like snacks. Daily: Eggs and dairies. Fresh: Fruit, Meat, Fish and Ready meals.  Living: Household essentials such as daily consumables.Hardware: Durables - such as home electronics. Apparel: Innerwear, outerwear.</Paragraph>
      <Paragraph>*Stores converted to “TRIAL SEIYU” format are counted as &quot;SEIYU&quot; stores, but the total number of stores within SEIYU remains.</Paragraph>
      <Spacer h={{ sp: 30, pc: 80 }} />
      <section>
        <SubHeading>Overview</SubHeading>
        <Spacer h={{ sp: 20, pc: 36 }} />
        <Paragraph>
          {monthlySales.summaryEN.split('→').map((value, index) => (
            <Fragment key={`${index}${value}`}>
              {value}
              <br />
            </Fragment>
          ))}
        </Paragraph>
        <Spacer h={{ sp: 15, pc: 30 }} />
        <Annotation>
          {monthlySales.commentsEN.split('→').map((value, index) => (
            <Fragment key={`${index}${value}`}>
              {value}
              <br />
            </Fragment>
          ))}
        </Annotation>
      </section>
    </>
  )
}

const SubHeading = styled(BHeading)`
  margin-top: 0;
  margin-bottom: 0;
`

const PublicationDate = styled(ChartAnnotation)`
  text-align: right;
`

const SmallTd = styled(Td)`
  //padding: 11px 8.5px 14px 8.5px;
  height:50px;

  &:nth-of-type(4),
  &:nth-of-type(8),
  &:nth-of-type(13),
  &:nth-of-type(17) {
    min-width: 48px;
    background: ${theme.colors.th.tertiary};
  }

  &:nth-of-type(9),
  &:nth-of-type(18),
  &:nth-of-type(19) {
    background: ${theme.colors.th.secondary};
  }
`

const SmallTh = styled(Th)`
  // padding: 11px 9px 14px 9px;
  height:50px;
`

const SmallTd2 = styled(Td)`
  // padding: 9px 8.5px 12px 8.5px;
  height:50px;

  &:nth-of-type(4),
  &:nth-of-type(8),
  &:nth-of-type(13),
  &:nth-of-type(17) {
    min-width: 48px;
    background: ${theme.colors.th.tertiary};
  }

  &:nth-of-type(9),
  &:nth-of-type(18),
  &:nth-of-type(19) {
    background: ${theme.colors.th.secondary};
  }
`

const SmallTh2 = styled(Th)`
  // padding: 9px 9px 12px 9px;
  height:50px;
`

const QuarterTh = styled(Th)`
  min-width: 48px;
`

const NoWrapLabel = styled.span`
  @media screen and (min-width: 1048px) {
    white-space: nowrap;
  }
`

const RotatedHeaderLabel = styled.span`
  display: inline-block;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  white-space: nowrap;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`
